import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Award, Plus, X } from "lucide-react";
import { CONSULTANTS } from "../data";

const reveal = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const Card = ({ person, i, onMore }) => (
  <motion.article
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    transition={{ delay: (i % 4) * 0.08 }}
    data-testid={`consultant-${person.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="group border border-cream/10 bg-forest p-7 text-center hover:border-amber/40 transition-colors duration-500 flex flex-col"
  >
    <img
      src={person.photo}
      alt={person.name}
      className="w-24 h-24 rounded-full object-cover object-top mx-auto ring-2 ring-amber/50 group-hover:ring-amber transition-[box-shadow] duration-500"
    />
    <h3 className="font-display text-xl text-cream mt-5 tracking-tight">{person.name}</h3>
    <p className="text-[10px] tracking-[0.18em] uppercase text-amber mt-1.5 leading-relaxed">{person.role}</p>
    <p className="text-xs text-cream/60 mt-4 leading-relaxed line-clamp-3 flex-1">{person.short}</p>
    {person.more && (
      <button
        data-testid={`consultant-more-${person.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onMore(person)}
        className="mt-5 mx-auto flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-amber border border-amber/40 px-4 py-2 hover:bg-amber hover:text-night transition-colors duration-300"
      >
        <Plus size={12} /> Read More
      </button>
    )}
  </motion.article>
);

const DetailModal = ({ person, onClose }) => (
  <AnimatePresence>
    {person && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-night/90 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
        onClick={onClose}
        data-testid="consultant-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.97 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-forest border border-amber/30 max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8 md:p-12"
        >
          <button
            data-testid="consultant-modal-close"
            onClick={onClose}
            className="absolute top-4 right-4 text-cream/50 hover:text-amber transition-colors"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
            <img src={person.photo} alt={person.name} className="w-28 h-28 rounded-full object-cover object-top ring-2 ring-amber shrink-0" />
            <div className="text-center sm:text-left">
              <h3 className="font-display text-2xl md:text-3xl text-cream tracking-tight">{person.name}</h3>
              <p className="text-[10px] tracking-[0.18em] uppercase text-amber mt-2 leading-relaxed">{person.role}</p>
            </div>
          </div>
          {person.more.bio.map((p, i) => (
            <p key={i} className="text-sm text-cream/70 leading-relaxed mb-4">{p}</p>
          ))}
          {person.more.highlights && (
            <ul className="mt-6 space-y-2.5 border-t border-cream/10 pt-6">
              {person.more.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2.5 text-xs text-cream/65 leading-relaxed">
                  <Award size={13} className="text-amber shrink-0 mt-0.5" />
                  {h}
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Consultants = () => {
  const [selected, setSelected] = useState(null);
  return (
    <section id="consultants" data-testid="consultants-section" className="relative bg-night py-28 md:py-40 border-t border-cream/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }} className="mb-16 md:mb-20">
          <p className="text-xs tracking-[0.3em] uppercase text-amber mb-6" data-testid="consultants-label">
            Learning Consultants Panel
          </p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight text-cream leading-[1.05] max-w-3xl">
            Guided by <span className="italic text-amber">world-class minds.</span>
          </h2>
          <p className="mt-6 max-w-xl text-base md:text-lg text-cream/70 leading-relaxed">
            Every LUSH programme is designed and delivered with a panel of internationally
            credentialed consultants, master facilitators and academic experts.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {CONSULTANTS.map((p, i) => (
            <Card key={p.name} person={p} i={i} onMore={setSelected} />
          ))}
        </div>
      </div>

      <DetailModal person={selected} onClose={() => setSelected(null)} />
    </section>
  );
};
